import { useQuery } from '@tanstack/react-query';
import type { MeResponse, Role } from '@mmf/contracts';
import { ApiError, fetchMe } from '../api/client';

/** Not exported — nothing outside this file needs the type by name, only useMe()'s inferred return. */
interface MeState {
  principal: MeResponse | undefined;
  isLoading: boolean;
  /** True when there is no valid session — render the sign-in screen. */
  isSignedOut: boolean;
  /** True when signed in but refused access, e.g. membership removed. */
  isRefused: boolean;
  error: Error | null;
}

export function useMe(): MeState {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: (failureCount, error) => {
      // A 401 or 403 is an answer, not a failure. Retrying it just delays the
      // sign-in screen and hammers the API.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    // Roles can change under the user — a demotion, a suspension. Refetching on
    // focus keeps the UI honest without polling.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const status = query.error instanceof ApiError ? query.error.status : undefined;

  return {
    principal: query.data,
    isLoading: query.isLoading,
    isSignedOut: status === 401,
    isRefused: status === 403,
    error: query.error,
  };
}

export function hasRole(principal: MeResponse | undefined, role: Role): boolean {
  return principal?.roles.includes(role) ?? false;
}

/** True when the principal holds any one of the given roles — same "any one is enough" rule as the API's @Roles(). */
export function hasAnyRole(principal: MeResponse | undefined, roles: Role[]): boolean {
  return roles.some((role) => hasRole(principal, role));
}
